import { Accordion, Alert, Center, Divider, Group, ScrollArea, Stack, Text } from '@mantine/core';
import React, { useState } from 'react';
import useRetrieveExtractEntities from '@/hooks/api/extractExecutions/useRetrieveExtractEntities.ts';
import StatusIndicator from '@/components/StatusIndicator.tsx';
import ExtractEntityRenderer from '@/pages/Run/components/ExtractResults/ExtractEntityRenderer.tsx';
import RerunMenu from '@/components/RerunMenu.tsx';
import CustomSpinner from '@/components/Spinner.tsx';
import RunInlineEmptyState from '@/pages/Run/components/RunInlineEmptyState.tsx';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import { Run } from '@/types/runs.ts';
import { StepExecution, StepRun } from '@/types/stepExecutions.ts';

const DEFAULT_ENTITY_NAME = '__default_entity__';

type props = {
  run: Run;
  stepRun?: StepRun;
  stepExecution: StepExecution;
  isDataViewer?: boolean;
};

const ExtractResultsRenderer = ({
  stepExecution,
  run,
  stepRun,
  isDataViewer = false,
}: props) => {
  const {
    data: extractEntities,
    isError,
    isFetching,
    refetch,
  } = useRetrieveExtractEntities(stepExecution?.id);

  const [showChildren, setShowChildren] = useState(isDataViewer);
  const entityResults = extractEntities?.results;
  const hasEntities = (entityResults?.length ?? 0) > 0;

  if (isError) {
    return (
      <Alert color="orange" title="Extraction results unavailable" role="alert">
        <Stack gap="xs" align="flex-start">
          <Text size="sm">The extracted entities could not be loaded.</Text>
          <StyledButton size="sm" variant="outline" loading={isFetching} onClick={() => void refetch()}>
            Retry
          </StyledButton>
        </Stack>
      </Alert>
    );
  }

  if (!stepExecution || !entityResults) {
    return (
      <Center w={'100%'} h={'100%'} p={'xl'} bg={'white'} style={{ borderRadius: '6px' }}>
        <CustomSpinner size={'md'} />
      </Center>
    );
  } else if (
    entityResults.length === 1 &&
    entityResults[0].identifier === DEFAULT_ENTITY_NAME
  ) {
    return (
      <ExtractEntityRenderer
        extractEntity={entityResults[0]}
        run={run}
        stepRun={stepRun}
        isDataViewer={isDataViewer}
        defaultShowChildren={isDataViewer}
      />
    );
  }
  return (
    <>
      {/*{!isDataViewer && (*/}
      {/*  <StepExecutionLogs*/}
      {/*    stepExecution={stepExecution}*/}
      {/*    enabled={enabled}*/}
      {/*    logsType={'extract_errors'}*/}
      {/*  />*/}
      {/*)}*/}
      <Accordion
        mt={isDataViewer ? 0 : '-4px'}
        mb={isDataViewer ? 0 : '-4px'}
        pb={isDataViewer && showChildren ? 'md' : 0}
        value={showChildren ? 'children' : null}
        onChange={(value) => setShowChildren(value === 'children')}
        // value={showChildren ? 'children' : null}
        // onChange={(value) => setShowChildren(value === 'children')}
      >
        <Accordion.Item value={'children'} style={{ borderBottom: '0px' }}>
          <Accordion.Control>
            <Group wrap={'nowrap'} gap={'xs'}>
              {!isDataViewer && (
                <Center>
                  <StatusIndicator
                    status={stepExecution.status}
                    terminalReasonCode={stepExecution.terminal_reason_code}
                    terminalReasonContext={stepExecution.terminal_reason_context}
                    currentStepPath={stepRun?.full_path}
                    include_text={false}
                  />
                </Center>
              )}
              <Text size="sm">
                {/*{!extractEntities?.results ? (*/}
                {/*  <b>Finding Entities</b>*/}
                {/*) : (*/}
                <b>
                  {entityResults.length === 1
                    ? '1 Entity'
                    : `${entityResults.length} Entities`}
                </b>
                {/*)}*/}
              </Text>
              <Group ml={'auto'} mr={'xs'} gap={'4px'}>
                {stepRun && (
                  <RerunMenu
                    run={run}
                    stepRun={stepRun}
                    // rerunAllData={{ metadata: { entities_to_run: 'all' } }}
                    rerunFailedData={{ extractEntitiesToRerun: 'failed' }}
                  />
                )}
              </Group>
            </Group>
          </Accordion.Control>
          <Accordion.Panel styles={{ content: { padding: 0 } }}>
            {hasEntities ? (
              <ScrollArea.Autosize mah={!isDataViewer ? 400 : Infinity} h={'100%'}>
                {entityResults.map((extractEntity, idx) => (
                  <span key={idx}>
                    {idx > 0 && <Divider mb={'0'} />}
                    <ExtractEntityRenderer
                      extractEntity={extractEntity}
                      run={run}
                      stepRun={stepRun}
                      isDataViewer={isDataViewer}
                      defaultShowChildren={isDataViewer && entityResults.length < 5}
                    />
                  </span>
                ))}
              </ScrollArea.Autosize>
            ) : (
              <RunInlineEmptyState message="No entities found" />
            )}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </>
  );
};

export default ExtractResultsRenderer;
